import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { Alert, Checkbox, Divider, FormControlLabel, Link } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import * as React from 'react'

import { useAnalytics } from '../../contexts/AnalyticsContext'
import db from '../../db/db'
import initFirebase from '../../db/initFirebase'
import routes from '../../pages/routes'
import { AnalyticsService } from '../../services/AnalyticsService'
import { AnalyticsEvents, AuthEventParams } from '../../types/AnalyticsEvents'
import {
  signInWithOAuth,
  processOAuthUserCreation,
  OAuthProviderType
} from '../../utils/oauthHelpers'

import PreLoginCard from './PreLoginCard'

export default function SignUp() {
  const { trackEvent } = useAnalytics()
  const [authError, setAuthError] = React.useState<string>('')
  const [capacityWarning, setCapacityWarning] = React.useState<string>('')
  const [agreedToTerms, setAgreedToTerms] = React.useState<boolean>(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)

  const handleOAuthSignUp = async (provider: OAuthProviderType) => {
    if (!agreedToTerms) {
      setAuthError('You must agree to the Terms of Service to sign up')
      return
    }

    setIsLoading(true)
    setAuthError('')

    const result = await signInWithOAuth(provider)

    if (result.success && result.userCredential) {
      try {
        const { rejectedOffices } = await processOAuthUserCreation(
          result.userCredential,
          myTheme
        )

        if (rejectedOffices.length > 0) {
          const warningMessage = `Note: You were invited to ${rejectedOffices.join(
            ', '
          )} but ${
            rejectedOffices.length === 1 ? 'it has' : 'they have'
          } reached capacity. Contact the office manager to upgrade.`
          localStorage.setItem('pendingCapacityWarning', warningMessage)
          setCapacityWarning(warningMessage)
        }

        // Track signup event with authentication method
        const authMethod = provider === 'google' ? 'google' : 'other'
        trackEvent(AnalyticsEvents.SIGN_UP, {
          method: authMethod
        } as AuthEventParams)

        // Set user ID and properties for analytics
        const user = result.userCredential.user
        AnalyticsService.setUserId(user.uid)
        AnalyticsService.setUserProperties({
          role: 'user',
          subscription_tier: 'free'
        })

        localStorage.setItem('userCreationComplete', 'true')
      } catch (err) {
        console.error('Error creating user record:', err)
        setIsLoading(false)
        setAuthError('Failed to create user account. Please try again.')
      }
    } else {
      setIsLoading(false)
      setAuthError(result.error || 'Authentication failed')
    }
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!agreedToTerms) {
      setAuthError('You must agree to the Terms of Service to sign up')
      return
    }

    const data = new FormData(event.currentTarget)

    const auth = initFirebase.getFireBaseStore().auth
    var email = data.get('email')?.toString() ?? ''
    var password = data.get('password')?.toString() ?? ''
    var allowEmail = (data.get('allowExtraEmails')?.toString() ?? '') === 'true'
    createUserWithEmailAndPassword(auth, email, password)
      .then(async userCredential => {
        // Signed in
        const user = userCredential.user
        console.log('Adding user', user)

        // Process pending invites
        const officeIdsToAdd: string[] = []
        const rejectedOffices: string[] = []

        try {
          // Query for pending invites by email
          const pendingInvites = await db.query({
            collection: 'invite',
            where: ['email', '==', email.toLowerCase()]
          })

          console.log(
            `Found ${pendingInvites.length} pending invites for ${email}`
          )

          // Check capacity for each invited office
          const { canAddUser } = await import(
            '../../services/subscriptionService'
          )

          for (const invite of pendingInvites) {
            if (invite.oid) {
              try {
                const hasCapacity = await canAddUser(invite.oid)

                if (hasCapacity) {
                  officeIdsToAdd.push(invite.oid)
                  console.log(`Adding user to office ${invite.oid}`)

                  // Track office invitation accepted event
                  AnalyticsService.trackEvent(AnalyticsEvents.INVITE_ACCEPTED, {
                    invitation_type: 'office'
                  })
                } else {
                  // Get office name for better error message
                  const office = await db.getById('offices', invite.oid)
                  rejectedOffices.push(office?.name || invite.oid)
                  console.log(`Office ${invite.oid} is at capacity, skipping`)
                }

                // Delete the processed invite
                if (invite.id) {
                  await db.deleteItem('invite', invite.id)
                }
              } catch (err) {
                console.error(
                  `Error processing invite for office ${invite.oid}:`,
                  err
                )
              }
            }
          }

          // Update pending office manager records (only for offices user was added to)
          const pendingManagerRecords = await db.query({
            collection: 'officeManagers',
            where: ['email', '==', email.toLowerCase()]
          })

          for (const record of pendingManagerRecords) {
            if (
              record.id &&
              record.officeId &&
              (!record.userId || record.userId === null)
            ) {
              // Only process manager records for offices the user was successfully added to
              if (!officeIdsToAdd.includes(record.officeId)) {
                // User was not added to this office (capacity issue), delete the pending manager record
                await db.deleteItem('officeManagers', record.id)
                console.log(
                  `Deleted pending office manager record for office ${record.officeId} (user not added due to capacity)`
                )
                continue
              }

              const newId = `${record.officeId}_${user.uid}`

              // Create new record with user ID
              const newManagerData = {
                officeId: record.officeId,
                userId: user.uid,
                email: email.toLowerCase(),
                addedBy: record.addedBy || user.uid,
                addedOnDate: record.addedOnDate || new Date()
              }

              const { getFirestore, doc, setDoc } = await import(
                'firebase/firestore'
              )
              const firestore = getFirestore()
              await setDoc(
                doc(firestore, 'officeManagers', newId),
                newManagerData
              )

              // Delete old record
              await db.deleteItem('officeManagers', record.id)
              console.log(
                `Updated office manager record for office ${record.officeId}`
              )
            }
          }

          // Show warning if any offices were rejected due to capacity
          if (rejectedOffices.length > 0) {
            const warningMessage = `Note: You were invited to ${rejectedOffices.join(
              ', '
            )} but ${
              rejectedOffices.length === 1 ? 'it has' : 'they have'
            } reached capacity. Contact the office manager to upgrade.`
            console.log(`⚠️  Setting capacity warning: ${warningMessage}`)
            // Store warning in localStorage to show after redirect
            localStorage.setItem('pendingCapacityWarning', warningMessage)
            setCapacityWarning(warningMessage)
          } else {
            console.log('✅ No rejected offices, no capacity warning needed')
          }
        } catch (err) {
          console.error('Error processing pending invites:', err)
          // Don't fail signup if invite processing fails
        }

        // Create user record with processed office IDs
        // Use the Auth UID as the document ID so security rules can look up the user
        await db.addItem('users', {
          id: user.uid,
          email: user.email,
          uid: user.uid,
          firstname: data.get('firstName')?.toString(),
          lastname: data.get('lastName')?.toString(),
          marketing: allowEmail,
          officeids: officeIdsToAdd,
          theme: myTheme,
          isAdmin: false,
          createdAt: new Date()
        } as any)

        console.log(`✅ Done adding user with ${officeIdsToAdd.length} offices`)

        // Track signup event with authentication method
        trackEvent(AnalyticsEvents.SIGN_UP, {
          method: 'email'
        } as AuthEventParams)

        // Set user ID and properties for analytics
        AnalyticsService.setUserId(user.uid)
        AnalyticsService.setUserProperties({
          role: 'user',
          subscription_tier: 'free'
        })

        // Signal that user creation is complete
        localStorage.setItem('userCreationComplete', 'true')
      })
      .catch(error => {
        console.error('❌ Error during signup:', error)
        var errorCode =
          error.code?.toString().substring(5) ||
          error.message ||
          'Unknown error'
        if (errorCode === 'weak-password')
          errorCode = 'Password needs to be at least 6 characters'
        setAuthError(errorCode)
      })
  }

  const myTheme = 'light'

  return (
    <PreLoginCard>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Avatar sx={{ bgcolor: '#2563eb', width: 40, height: 40 }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography
            component='h1'
            variant='h5'
            sx={{ fontWeight: 700, color: '#1e293b' }}
          >
            Sign up
          </Typography>
        </Box>

        {/* Terms of Service Checkbox */}
        <Box sx={{ width: '100%', mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreedToTerms}
                onChange={e => setAgreedToTerms(e.target.checked)}
                color='primary'
              />
            }
            label={
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                I agree to the{' '}
                <Link
                  href={routes.termsOfService}
                  target='_blank'
                  sx={{
                    color: 'primary.main',
                    fontWeight: 500,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Terms of Service
                </Link>
              </Typography>
            }
          />
        </Box>

        {/* OAuth Buttons */}
        <Box sx={{ width: '100%', mb: 2 }}>
          <Button
            fullWidth
            variant='outlined'
            onClick={() => handleOAuthSignUp('google')}
            disabled={isLoading || !agreedToTerms}
            startIcon={
              <Box
                component='img'
                src='https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg'
                alt='Google'
                sx={{ width: 20, height: 20 }}
              />
            }
            sx={{
              mb: 1.5,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#dadce0',
              color: '#3c4043',
              '&:hover': {
                borderColor: '#d2d3d4',
                backgroundColor: '#f8f9fa'
              },
              '&:disabled': {
                borderColor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            Continue with Google
          </Button>

          <Button
            fullWidth
            variant='outlined'
            onClick={() => handleOAuthSignUp('microsoft')}
            disabled={isLoading || !agreedToTerms}
            startIcon={
              <Box
                component='img'
                src='https://learn.microsoft.com/en-us/entra/identity-platform/media/howto-add-branding-in-apps/ms-symbollockup_mssymbol_19.svg'
                alt='Microsoft'
                sx={{ width: 20, height: 20 }}
              />
            }
            sx={{
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#8c8c8c',
              color: '#5e5e5e',
              '&:hover': {
                borderColor: '#737373',
                backgroundColor: '#f3f2f1'
              },
              '&:disabled': {
                borderColor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            Continue with Microsoft
          </Button>
        </Box>

        <Divider sx={{ width: '100%', mb: 2 }}>
          <Typography variant='body2' sx={{ color: 'text.secondary', px: 2 }}>
            or
          </Typography>
        </Divider>

        <Box
          component='form'
          noValidate
          onSubmit={handleSubmit}
          sx={{ mt: 1, width: '100%' }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoComplete='given-name'
                name='firstName'
                required
                fullWidth
                id='firstName'
                label='First Name'
                autoFocus
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff !important'
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b'
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#2563eb'
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#1e293b'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 100px #ffffff inset !important',
                    WebkitTextFillColor: '#1e293b !important',
                    caretColor: '#1e293b'
                  },
                  '& input:-webkit-autofill:hover': {
                    WebkitBoxShadow: '0 0 0 100px #ffffff inset !important'
                  },
                  '& input:-webkit-autofill:focus': {
                    WebkitBoxShadow: '0 0 0 100px #ffffff inset !important'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id='lastName'
                label='Last Name'
                name='lastName'
                autoComplete='family-name'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff !important'
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b'
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#2563eb'
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#1e293b'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 100px #ffffff inset !important',
                    WebkitTextFillColor: '#1e293b !important',
                    caretColor: '#1e293b'
                  },
                  '& input:-webkit-autofill:hover': {
                    WebkitBoxShadow: '0 0 0 100px #ffffff inset !important'
                  },
                  '& input:-webkit-autofill:focus': {
                    WebkitBoxShadow: '0 0 0 100px #ffffff inset !important'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id='email'
                label='Email Address'
                name='email'
                autoComplete='email'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff !important'
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b'
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#2563eb'
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#1e293b'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 100px #ffffff inset !important',
                    WebkitTextFillColor: '#1e293b !important',
                    caretColor: '#1e293b'
                  },
                  '& input:-webkit-autofill:hover': {
                    WebkitBoxShadow: '0 0 0 100px #ffffff inset !important'
                  },
                  '& input:-webkit-autofill:focus': {
                    WebkitBoxShadow: '0 0 0 100px #ffffff inset !important'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                name='password'
                label='Password'
                type='password'
                id='password'
                autoComplete='new-password'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff !important'
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b'
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#2563eb'
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#1e293b'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)'
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 100px #ffffff inset !important',
                    WebkitTextFillColor: '#1e293b !important',
                    caretColor: '#1e293b'
                  },
                  '& input:-webkit-autofill:hover': {
                    WebkitBoxShadow: '0 0 0 100px #ffffff inset !important'
                  },
                  '& input:-webkit-autofill:focus': {
                    WebkitBoxShadow: '0 0 0 100px #ffffff inset !important'
                  }
                }}
              />
            </Grid>
          </Grid>
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disabled={!agreedToTerms || isLoading}
            sx={{
              mt: 3,
              mb: 2,
              backgroundColor: '#2563eb',
              color: '#ffffff',
              fontWeight: 600,
              padding: '12px',
              fontSize: '1rem',
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#1e40af'
              }
            }}
          >
            {isLoading ? 'Signing Up...' : 'Sign Up'}
          </Button>
          {authError !== '' && (
            <Alert
              severity='error'
              onClose={() => setAuthError('')}
              sx={{ mb: 2 }}
            >
              {authError}
            </Alert>
          )}
          {capacityWarning !== '' && (
            <Alert
              severity='warning'
              onClose={() => setCapacityWarning('')}
              sx={{ mb: 2 }}
            >
              {capacityWarning}
            </Alert>
          )}
          <Grid container justifyContent='flex-end'>
            <Grid item>
              <Link
                href='/signin'
                variant='body2'
                sx={{
                  color: '#2563eb',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Already have an account? Sign in
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </PreLoginCard>
  )
}
